import { Button, makeStyles } from "@material-ui/core";

import { AccordionList } from "../components/accordion/AccordionList";
import { AccordionListItem } from "../components/accordion/AccordionListItem";
import { UnorderedList } from "../components/unordered-list/UnorderedList";
import { useAppointmentTaskGuideDialog } from "./AppointmentTaskGuide";
import { useNewTaskGuideDialog } from "./NewTaskGuide";
import { useOnboardingGuideDialog } from "./OnboardingGuideDialog";
import { useRegistrationTaskGuideDialog } from "./RegistrationTaskGuide";
import { appointmentTaskGuideContentBullets, newTaskGuideContentBullets, onboardingGuideContentBullets, registrationTaskGuideContentBullets } from "./TutorialPage";
import { GardenerDocument, Guide } from "../gardener/gardener.model";
import { useCallback } from "react";
import { TaskResponseData } from "../task/task-response-action.hook";

const useStyles = makeStyles({
    content: {
        fontSize: "16px",
        fontWeight: 400,
        color: "#4A4A4A",

        "& > *": {
            marginTop: 0,
            marginBottom: "16px",

            "&:last-child": {
                marginBottom: 0,
            },
        },

        "& > ul": {
            marginTop: "-16px",

            "&:first-child": {
                marginTop: 0,
            },
        },

        "& > button": {
            padding: 0,
            fontSize: "16px",
            fontWeight: 500,
            color: "#297A1D",
            textDecoration: "none",
            textTransform: "none",
    
            "&:hover": {
                backgroundColor: "transparent",
                color: "#1C5B13",
                textDecoration: "underline",
            },
        },
    },
});

export interface OverviewGuideListProps {
    gardener: GardenerDocument;
    taskResponseAction: (data: TaskResponseData | string) => void;
}

export const OverviewGuideList = (props: OverviewGuideListProps) => {
    const classes = useStyles();
    const { gardener, taskResponseAction } = props;

    const markLookupGuideCompleted = useCallback((guide: Guide) => {
        if ( gardener.tutorial.completedGuides.includes(guide) ) return;

        taskResponseAction({ action: "mark guide completed", mode: "lookup", guide });
    }, [taskResponseAction, gardener]);

    const markOnboardingGuideCompleted = useCallback(() => markLookupGuideCompleted("onboarding"), [markLookupGuideCompleted]);
    const startOnboardingGuide = useOnboardingGuideDialog(markOnboardingGuideCompleted);

    const markNewTaskGuideCompleted = useCallback(() => markLookupGuideCompleted("newTask"), [markLookupGuideCompleted]);
    const startNewTaskGuide = useNewTaskGuideDialog(markNewTaskGuideCompleted);

    const markAppointmentTaskCompleted = useCallback(() => markLookupGuideCompleted("appointmentTask"), [markLookupGuideCompleted]);
    const startAppointmentTaskGuide = useAppointmentTaskGuideDialog(markAppointmentTaskCompleted);

    const markRegistrationTaskGuideCompleted = useCallback(() => markLookupGuideCompleted("registrationTask"), [markLookupGuideCompleted]);
    const startRegistrationTaskGuide = useRegistrationTaskGuideDialog(markRegistrationTaskGuideCompleted);

    return (
        <AccordionList>

            <AccordionListItem id="overview-guide-1" title="Generelt om konceptet">
                <div className={classes.content}>
                    <p>Denne guide handler om:</p>
                    <UnorderedList>
                        {onboardingGuideContentBullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
                    </UnorderedList>
                    <Button disableRipple onClick={startOnboardingGuide}>Start guide</Button>
                </div>
            </AccordionListItem>

            <AccordionListItem id="overview-guide-2" title="Sådan besvarer du “Ny opgave”">
                <div className={classes.content}>
                    <p>Denne guide handler om:</p>
                    <UnorderedList>
                        {newTaskGuideContentBullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
                    </UnorderedList>
                    <Button disableRipple onClick={startNewTaskGuide}>Start guide</Button>
                </div>
            </AccordionListItem>

            <AccordionListItem id="overview-guide-3" title="Sådan besvarer du “Har I lavet en aftale?”">
                <div className={classes.content}>
                    <p>Denne guide handler om:</p>
                    <UnorderedList>
                        {appointmentTaskGuideContentBullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
                    </UnorderedList>
                    <Button disableRipple onClick={startAppointmentTaskGuide}>Start guide</Button>
                </div>
            </AccordionListItem>

            <AccordionListItem id="overview-guide-4" title="Sådan besvarer du “Hvordan gik dit besøg?”">
                <div className={classes.content}>
                    <p>Denne guide handler om:</p>
                    <UnorderedList>
                        {registrationTaskGuideContentBullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
                    </UnorderedList>
                    <Button disableRipple onClick={startRegistrationTaskGuide}>Start guide</Button>
                </div>
            </AccordionListItem>

        </AccordionList>
    );
};
